import { useEffect, useMemo, useRef } from 'react';
import DOMPurify from 'dompurify';
import katex from 'katex';
import 'katex/contrib/mhchem';
import 'katex/dist/katex.min.css';
import './richtext/richtext.css';

interface Props {
  html: string;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * Renders sanitized question-prompt HTML and turns persisted math placeholders
 * (`<span data-math="...">`) into rendered KaTeX. Authors are trusted (faculty/admin)
 * but we sanitize anyway so a bad paste can never inject script into a student's page.
 */
export default function RichTextView({ html, className, style }: Props) {
  const ref = useRef<HTMLDivElement>(null);

  const clean = useMemo(
    () =>
      DOMPurify.sanitize(html || '', {
        ADD_ATTR: ['target', 'data-math', 'data-display', 'colspan', 'rowspan', 'colwidth'],
        ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
      }),
    [html],
  );

  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    root.querySelectorAll<HTMLElement>('span[data-math]').forEach((el) => {
      const latex = el.getAttribute('data-math') || '';
      const display = el.getAttribute('data-display') === 'true';
      try {
        el.innerHTML = katex.renderToString(latex || '\\,', { displayMode: display, throwOnError: false });
      } catch {
        el.textContent = latex;
      }
    });
  }, [clean]);

  return (
    <div
      ref={ref}
      className={`rte-view rte-doc${className ? ' ' + className : ''}`}
      style={style}
      dangerouslySetInnerHTML={{ __html: clean }}
    />
  );
}
